import {
  type InfiniteData,
  type QueryClient,
  type QueryFunctionContext,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import { computed, type MaybeRef, reactive, type Ref, unref } from "vue";

import type { components as OpenAPI } from "@/autogen/openapi";
import { ok } from "@/lib/openapi";
import type { SearchResultWithId } from "@/types/search_result";

import { unique } from "@/lib/collections";
import api from "../../base";
import { createBatchedFetch } from "../batch-fetch";
import {
  findItemInInfiniteQueries,
  findItemInListQueries,
  useNewsletterId,
} from "../utils";
import type { EmailPage } from "./list";

export type Email = OpenAPI["schemas"]["Email"];

type EmailCreateInput = OpenAPI["schemas"]["EmailInput"];
type EmailUpdateInput = OpenAPI["schemas"]["EmailUpdateInput"] & {
  id: string;
  confirmPotentialDuplicate?: boolean;
};

const fetchEmail = createBatchedFetch<string, Email>({
  limit: 50,
  idFromResource(resource) {
    return resource.id;
  },
  async fetch(ids, signal) {
    const data = await ok(
      api.get("/emails", {
        signal,
        params: {
          query: {
            ids: ids,
          },
        },
      })
    );

    return data.results;
  },
});

const findFallbackEmail = (
  queryClient: QueryClient,
  newsletterId: string,
  emailId: string
): Email | undefined => {
  // Listing
  {
    const queries = queryClient.getQueriesData<InfiniteData<EmailPage>>({
      queryKey: [newsletterId, "emails", "list"],
    });

    const found = findItemInInfiniteQueries(queries, (email) => {
      return email.id === emailId;
    });

    if (found) {
      return found;
    }
  }

  // Search
  {
    const queries = queryClient.getQueriesData<SearchResultWithId[]>({
      queryKey: [newsletterId, "search"],
    });

    type EmailSearchResult = SearchResultWithId & { type: "email" };

    // this would've looked nicer if TypeScript didn't lose the type narrowing
    // after adding the `.obj.id` condition :(
    const found = findItemInListQueries(
      queries,
      (item): item is EmailSearchResult => {
        return item.type === "email" && item.obj.id === emailId;
      }
    );

    if (found) {
      return found.obj satisfies Email;
    }
  }
};

export const useEmailDetailQueries = ({ ids }: { ids: MaybeRef<string[]> }) => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const uniqueIds = computed(() => {
    return unique(unref(ids));
  });

  const queries = useQueries({
    queries: computed(() => {
      return uniqueIds.value.map((id) => {
        return {
          queryKey: [newsletterId, "emails", "detail", id],
          async queryFn({ signal }: QueryFunctionContext): Promise<Email> {
            return fetchEmail(id, signal);
          },
          placeholderData(): Email | undefined {
            return findFallbackEmail(queryClient, newsletterId.value!, id);
          },
        };
      });
    }),
    combine(result) {
      const ids = uniqueIds.value;

      return result.map((query, idx) => {
        return { id: ids[idx], query };
      });
    },
  });

  return queries;
};

export const useEmailDetailQuery = ({
  id,
  batch = false,
  enabled,
}: {
  id: string | Ref<string | undefined>;
  batch?: boolean;
  enabled?: Ref<boolean>;
}) => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const query = useQuery({
    queryKey: [newsletterId, "emails", "detail", id],
    enabled: computed(() => {
      return (unref(enabled) ?? true) && unref(id) !== undefined;
    }),
    async queryFn({ signal }): Promise<Email> {
      if (batch) {
        return fetchEmail(unref(id)!, signal);
      }

      const data = await ok(
        api.get("/emails/{id}", {
          signal,
          params: {
            path: {
              id: unref(id)!,
            },
          },
        })
      );

      return data;
    },
    placeholderData(): Email | undefined {
      const needle = unref(id);
      if (!needle) {
        return;
      }

      return findFallbackEmail(queryClient, newsletterId.value!, needle);
    },
  });

  return query;
};

export const useCreateEmailMutation = () => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const mutation = useMutation({
    async mutationFn(input: EmailCreateInput): Promise<Email> {
      const data = await ok(
        api.post("/emails", {
          body: input,
        })
      );

      return data;
    },
    onSuccess(data) {
      queryClient.setQueryData(
        [newsletterId, "emails", "detail", data.id],
        data
      );

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "emails"],
      });

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "search"],
      });
    },
  });

  return mutation;
};

// Modification dates from email saves made by this browser session — the
// editor's autosave, but also e.g. the finalization drawer scheduling a send.
// The editor uses this to avoid mistaking its own session's writes for edits
// made by another session.
export const ownSaveModificationDates = reactive(new Map<string, string>());

export const useUpdateEmailMutation = () => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const mutation = useMutation({
    async mutationFn({
      id,
      confirmPotentialDuplicate,
      ...input
    }: EmailUpdateInput): Promise<Email> {
      const data = await ok(
        api.patch("/emails/{id}", {
          params: {
            path: { id },
          },
          headers: confirmPotentialDuplicate
            ? {
                "X-Buttondown-Live-Dangerously": "true",
              }
            : undefined,
          body: input,
        })
      );

      return data;
    },
    onSuccess(data) {
      ownSaveModificationDates.set(data.id, data.modification_date);

      queryClient.setQueryData(
        [newsletterId, "emails", "detail", data.id],
        data
      );

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "emails"],
      });

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "search"],
      });
    },
  });

  return mutation;
};
